import { useEffect, useState, useCallback } from 'react'
import { CalendarClock, Plus, Edit3, Trash2, Star, MapPin, Beer, Radio, Settings, Trophy, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import PageHeader from '@/components/PageHeader'
import SubNav from '@/components/SubNav'
import { programmeAPI } from '@/lib/api'
import { useProgrammeSocket } from '@/lib/programmeSocket'

const TRACKS = {
  zaal:    { label: 'Zaal',                    icon: MapPin,         color: '#7BC8FF' },
  bar:     { label: 'Bar',                     icon: Beer,           color: '#FFB57A' },
  show:    { label: 'Show',                    icon: Radio,          color: '#B4FF39' },
  compo:   { label: 'Compo',                   icon: Trophy,         color: '#C77BFF' },
  kritiek: { label: 'Kritieke aankondigingen', icon: AlertTriangle,  color: '#FF5A5A' },
}

const LINK_KINDS = [
  { value: 'none',         label: 'Geen koppeling' },
  { value: 'tournament',   label: 'Tournament (vul id in)' },
  { value: 'kempenvision', label: 'Kempenvision' },
  { value: 'imagecompo',   label: 'Image Compo' },
  { value: 'url',          label: 'Externe URL' },
]

/** ISO-string → waarde voor <input type="datetime-local"> (lokale tijd, zonder Z). */
const toLocalInput = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fmtWhen = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('nl-BE', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export default function ProgrammeAdminPage() {
  const [slots, setSlots]   = useState([])
  const [loading, setLoad]  = useState(true)
  const [dialog, setDialog] = useState(null)
  const [busy, setBusy]     = useState(false)
  const [error, setError]   = useState(null)

  const load = useCallback(() => {
    programmeAPI.list()
      .then(r => setSlots(r.data?.slots ?? []))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoad(false))
  }, [])

  useEffect(() => { load() }, [load])
  useProgrammeSocket(load)

  const openNew = () => setDialog({
    track: 'zaal', title: '', description: '',
    starts_at: '', ends_at: '',
    link_kind: 'none', link_ref: '', is_highlight: false, admin_only: false,
  })

  const openEdit = (s) => setDialog({
    id: s.id,
    track: s.track || 'zaal',
    title: s.title || '',
    description: s.description || '',
    starts_at: toLocalInput(s.starts_at),
    ends_at: toLocalInput(s.ends_at),
    link_kind: s.link_kind || 'none',
    link_ref: s.link_ref || '',
    is_highlight: !!s.is_highlight,
    admin_only: !!s.admin_only,
  })

  const save = async (e) => {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      const payload = {
        track: dialog.track,
        title: dialog.title.trim(),
        description: dialog.description.trim() || null,
        starts_at: dialog.starts_at ? new Date(dialog.starts_at).toISOString() : null,
        ends_at:   dialog.ends_at   ? new Date(dialog.ends_at).toISOString()   : null,
        link_kind: dialog.link_kind === 'none' ? null : dialog.link_kind,
        link_ref:  dialog.link_kind === 'none' ? null : (dialog.link_ref.trim() || null),
        is_highlight: dialog.is_highlight,
        admin_only:   dialog.admin_only,
      }
      if (dialog.id) await programmeAPI.update(dialog.id, payload)
      else           await programmeAPI.create(payload)
      setDialog(null)
      load()
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setBusy(false) }
  }

  const del = async (id) => {
    if (!window.confirm('Dit moment verwijderen?')) return
    await programmeAPI.remove(id)
    load()
  }

  const clearAll = async () => {
    if (!window.confirm('De hele agenda van deze LAN wissen? Dit kan niet ongedaan gemaakt worden.')) return
    await programmeAPI.clear()
    load()
  }

  return (
    <div>
      <PageHeader
        icon={Settings}
        title="Agenda beheren"
        subtitle="Voeg momenten toe aan de programma-tijdlijn van de actieve LAN"
      />

      <div className="mb-4">
        <SubNav isAdmin items={[
          { to: '/programma',       label: 'Tijdlijn', icon: CalendarClock },
          { to: '/programma/admin', label: 'Agenda beheren', icon: Settings, adminOnly: true },
        ]} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Momenten ({slots.length})</CardTitle>
          <div className="flex gap-2">
            {slots.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} className="text-signal">Alles wissen</Button>
            )}
            <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Nieuw moment</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Laden…</p>}
          {!loading && slots.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nog geen momenten. Voeg er een toe — de tijdlijn vult zich meteen.
            </p>
          )}
          {slots.map(s => {
            const track = TRACKS[s.track] ?? TRACKS.zaal
            const Icon = track.icon
            return (
              <div key={s.id}
                   className="flex items-center gap-3 p-3 rounded-[4px] border border-border bg-card"
                   style={{ borderLeft: `2px solid ${track.color}` }}>
                <Icon className="h-4 w-4 shrink-0" style={{ color: track.color }} />
                <div className="w-44 shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                  {fmtWhen(s.starts_at)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{s.title}</span>
                    {s.is_highlight && <Star className="h-3.5 w-3.5 text-gold shrink-0" />}
                    {s.admin_only && (
                      <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-signal/40 bg-signal/10 text-signal shrink-0"
                            title="Alleen zichtbaar voor admins">
                        admin
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit3 className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => del(s.id)}><Trash2 className="h-4 w-4 text-signal" /></Button>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialog.id ? 'Moment bewerken' : 'Nieuw moment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label>Titel *</Label>
                <Input value={dialog.title} required
                       onChange={e => setDialog({ ...dialog, title: e.target.value })}
                       placeholder="CS 1.6 toernooi — kwartfinales" />
              </div>

              <div>
                <Label>Track</Label>
                <Select value={dialog.track} onValueChange={v => setDialog({ ...dialog, track: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRACKS).map(([k, t]) => (
                      <SelectItem key={k} value={k}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start *</Label>
                  <Input type="datetime-local" value={dialog.starts_at} required
                         onChange={e => setDialog({ ...dialog, starts_at: e.target.value })} />
                </div>
                <div>
                  <Label>Einde</Label>
                  <Input type="datetime-local" value={dialog.ends_at}
                         onChange={e => setDialog({ ...dialog, ends_at: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Omschrijving</Label>
                <textarea value={dialog.description} rows={2}
                          onChange={e => setDialog({ ...dialog, description: e.target.value })}
                          className="w-full rounded-[4px] border border-border bg-background px-3 py-2 text-sm"
                          placeholder="Korte toelichting die deelnemers in de tijdlijn zien." />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Koppeling</Label>
                  <Select value={dialog.link_kind} onValueChange={v => setDialog({ ...dialog, link_kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LINK_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(dialog.link_kind === 'tournament' || dialog.link_kind === 'url') && (
                  <div>
                    <Label>{dialog.link_kind === 'tournament' ? 'Tournament id' : 'URL'}</Label>
                    <Input value={dialog.link_ref}
                           onChange={e => setDialog({ ...dialog, link_ref: e.target.value })}
                           placeholder={dialog.link_kind === 'tournament' ? '12' : 'https://…'} />
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                <input type="checkbox" checked={dialog.is_highlight}
                       onChange={e => setDialog({ ...dialog, is_highlight: e.target.checked })} />
                Hoofdmoment — krijgt accent in de tijdlijn
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={dialog.admin_only}
                       onChange={e => setDialog({ ...dialog, admin_only: e.target.checked })} />
                <span>
                  Alleen voor admins zichtbaar
                  <span className="text-muted-foreground ml-1">— verbergt dit item op de publieke tijdlijn</span>
                </span>
              </label>

              {error && <p className="text-sm text-signal">{error}</p>}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit" disabled={busy}>{busy ? 'Opslaan…' : (dialog.id ? 'Opslaan' : 'Aanmaken')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
